/**
 * Where an organization's stored files live, and how a private one is named.
 *
 * Most organizations' files (policy uploads, replacement versions, templates,
 * decision proofs) go to the shared public Vercel Blob store, and their rows
 * carry the store's full URL. An organization whose contract restricts where
 * its data may be held (IOM FSA, Standard Bank, KPMG; `restrictedOrganizations.ts`)
 * keeps them instead in its own private Azure Blob container in UK South,
 * which only its dedicated workspace deployment can reach. Its rows then carry a reference, not a URL:
 *
 *   amoreg-file://<organization-id>/<pathname>
 *
 * A reference is not fetchable. Every reader resolves it through the
 * organization's workspace server (`organizationFiles.ts`), which checks the
 * caller's credential and the organization before it reads a byte; a reader
 * that forgets to fails closed instead of downloading from somewhere public.
 * The host is never in the reference: it comes from deployment configuration
 * (`AMOREG_ORG_WORKSPACE_ORIGINS`), exactly as every other credentialed call
 * to a dedicated deployment does.
 *
 * Isomorphic on purpose (no Node APIs), so a browser bundle may recognise a
 * reference too.
 *
 * @module organizationFileStorage
 */
import { IOMFSA_ORGANIZATION_ID, isRestrictedOrganization } from "./restrictedOrganizations.ts";
import { isOrganizationUuid, normalizeOrganizationId } from "./workspaceOrigins.ts";

/** Where an organization's new stored files are written. */
export type OrganizationFileStorage =
  /** The shared public Vercel Blob store; rows hold its URL. */
  | "public-blob"
  /** The organization's private container behind its workspace server; rows hold a reference. */
  | "organization-private";

/**
 * The one storage decision every writer makes. Chosen from the verified
 * organization of the request, never from anything the request names.
 */
export function organizationFileStorage(
  organizationId: string | null | undefined,
): OrganizationFileStorage {
  return organizationId && isRestrictedOrganization(normalizeOrganizationId(organizationId))
    ? "organization-private"
    : "public-blob";
}

export const ORGANIZATION_FILE_SCHEME = "amoreg-file:";
const REFERENCE_PREFIX = `${ORGANIZATION_FILE_SCHEME}//`;

/** The workspace server route that stores and serves private organization files. */
export const ORGANIZATION_FILES_PATH = "/api/amoreg/organization-files";

/** The credential the apps present to that route; nothing else accepts it. */
export const ORGANIZATION_FILES_TOKEN_ENV = "AMOREG_ORGANIZATION_FILES_TOKEN";

/** New files: one folder per organization, a random folder per file, the name kept. */
export const ORGANIZATION_FILES_PREFIX = "organization-files/";

/**
 * Verified copies of the organization's objects from the public Vercel Blob
 * stores, kept as `recovered-vercel-blob/<organization-id>/<store>/<pathname>`.
 * A container can hold several organizations (a regional deployment), so the
 * organization segment is what keeps one tenant's copies out of another's
 * references.
 */
export const RECOVERED_PUBLIC_BLOB_PREFIX = "recovered-vercel-blob/";
export const RECOVERED_PUBLIC_BLOB_STORES = ["amoreg-files-prod", "rulewise"] as const;
export type RecoveredPublicBlobStore = (typeof RECOVERED_PUBLIC_BLOB_STORES)[number];

/**
 * IOM FSA's copies were made before the organization segment, as
 * `recovered-vercel-blob/<store>/<pathname>`, into a container that holds only
 * IOM FSA. That form is accepted for IOM FSA alone and never written again.
 */
const UNNAMESPACED_RECOVERED_ORGANIZATION = IOMFSA_ORGANIZATION_ID;

/** Where a verified copy of a public object is kept in the organization's container. */
export function recoveredPublicBlobPathname(
  organizationId: string,
  store: RecoveredPublicBlobStore,
  pathname: string,
): string {
  return `${RECOVERED_PUBLIC_BLOB_PREFIX}${normalizeOrganizationId(organizationId)}/${store}/${pathname}`;
}

const isRecoveredStore = (segment: string | undefined): boolean =>
  (RECOVERED_PUBLIC_BLOB_STORES as ReadonlyArray<string>).includes(segment ?? "");

/** The longest pathname a reference may carry; Azure's own limit is 1,024. */
export const ORGANIZATION_FILE_PATHNAME_MAX = 1024;

export interface OrganizationFileReference {
  readonly organizationId: string;
  readonly pathname: string;
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

/**
 * Whether `pathname` is one an organization's reference may name: inside its
 * own `organization-files/<organization-id>/` folder or its own recovered copies,
 * with no traversal, empty segment, backslash or control character. Anything
 * else in the container (thread workspaces, manifests) is not reachable
 * through a file reference.
 */
export function isOrganizationFilePathname(organizationId: string, pathname: string): boolean {
  if (pathname.length === 0 || pathname.length > ORGANIZATION_FILE_PATHNAME_MAX) return false;
  if (CONTROL_CHARACTERS.test(pathname) || pathname.includes("\\")) return false;
  const segments = pathname.split("/");
  if (segments.some((segment) => segment.length === 0 || segment === "." || segment === "..")) {
    return false;
  }
  const own = `${ORGANIZATION_FILES_PREFIX}${normalizeOrganizationId(organizationId)}/`;
  if (pathname.startsWith(own)) return segments.length >= 4;
  if (pathname.startsWith(RECOVERED_PUBLIC_BLOB_PREFIX)) {
    const organization = normalizeOrganizationId(organizationId);
    if (segments[1] === organization) return segments.length >= 4 && isRecoveredStore(segments[2]);
    return (
      organization === UNNAMESPACED_RECOVERED_ORGANIZATION &&
      segments.length >= 3 &&
      isRecoveredStore(segments[1])
    );
  }
  return false;
}

/**
 * RFC 3986 strict: `encodeURIComponent` leaves `!'()*` bare, and a bare `)`
 * would end a markdown link early. A reference therefore holds only
 * unreserved characters, `%`, `/` and its scheme.
 */
const encodeSegment = (segment: string): string =>
  encodeURIComponent(segment).replace(
    /[!'()*]/g,
    (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`,
  );

/** `amoreg-file://<organization-id>/<pathname>`, the pathname's segments percent-encoded. */
export function formatOrganizationFileReference(reference: OrganizationFileReference): string {
  const organizationId = normalizeOrganizationId(reference.organizationId);
  if (!isOrganizationUuid(organizationId)) {
    throw new Error("An organization file reference needs a UUID organization.");
  }
  if (!isOrganizationFilePathname(organizationId, reference.pathname)) {
    throw new Error("An organization file reference names a pathname outside the organization.");
  }
  return `${REFERENCE_PREFIX}${organizationId}/${reference.pathname
    .split("/")
    .map(encodeSegment)
    .join("/")}`;
}

/** Whether a stored value is a private reference rather than a URL. Cheap; does not validate. */
// Deliberately not a `value is string` guard: a string that is not a
// reference would narrow to `never` in the other branch.
export const isOrganizationFileReference = (value: unknown): boolean =>
  typeof value === "string" && value.startsWith(REFERENCE_PREFIX);

/**
 * Parse a reference, or `null` for anything that is not a well-formed one:
 * a URL, a non-UUID organization, a pathname outside the organization, bad
 * percent-encoding.
 */
export function parseOrganizationFileReference(
  value: string | null | undefined,
): OrganizationFileReference | null {
  if (typeof value !== "string" || !isOrganizationFileReference(value)) return null;
  const rest = value.slice(REFERENCE_PREFIX.length);
  const slash = rest.indexOf("/");
  if (slash < 0) return null;
  const organizationId = rest.slice(0, slash);
  // The organization is written in lowercase; anything else was not formatted here.
  if (!isOrganizationUuid(organizationId) || organizationId !== organizationId.toLowerCase()) {
    return null;
  }
  let pathname: string;
  try {
    pathname = rest
      .slice(slash + 1)
      .split("/")
      .map((segment) => decodeURIComponent(segment))
      .join("/");
  } catch {
    return null;
  }
  // A segment that decoded into a slash would change the path's shape.
  if (rest.slice(slash + 1).split("/").length !== pathname.split("/").length) return null;
  if (!isOrganizationFilePathname(organizationId, pathname)) return null;
  return { organizationId, pathname };
}

/**
 * The reference, if it belongs to `organizationId`; `null` otherwise. The
 * check every reader makes before it asks for a byte: a reference copied into
 * another organization's row reads as nothing.
 */
export function organizationFileReferenceFor(
  organizationId: string,
  value: string | null | undefined,
): OrganizationFileReference | null {
  const parsed = parseOrganizationFileReference(value);
  return parsed !== null && parsed.organizationId === normalizeOrganizationId(organizationId)
    ? parsed
    : null;
}

/** A stored file name reduced to one safe path segment, its extension kept. */
export function organizationFileName(filename: string): string {
  const base = filename.split(/[\\/]/).pop() ?? "";
  const cleaned = base
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/[^\p{L}\p{N}._ ()-]+/gu, "_")
    .trim()
    .replace(/^\.+/, "");
  const name = cleaned.length > 0 ? cleaned : "file";
  // Counted and cut in code points: a UTF-16 slice could leave a lone
  // surrogate, which encodeURIComponent refuses.
  const characters = Array.from(name);
  if (characters.length <= 160) return name;
  const dot = characters.lastIndexOf(".");
  const extension = dot > 0 && characters.length - dot <= 16 ? characters.slice(dot) : [];
  return [...characters.slice(0, 160 - extension.length), ...extension].join("");
}

/** `organization-files/<organization-id>/<random>/<name>`: unguessable, never colliding. */
export function newOrganizationFilePathname(
  organizationId: string,
  filename: string,
  random: () => string = () => crypto.randomUUID(),
): string {
  return `${ORGANIZATION_FILES_PREFIX}${normalizeOrganizationId(organizationId)}/${random()}/${organizationFileName(filename)}`;
}

/** The file name a reference was stored under: its last path segment. */
export const organizationFileBasename = (reference: OrganizationFileReference): string =>
  reference.pathname.split("/").pop() ?? "file";

/**
 * Where a browser signed in to the organization's own workspace server reads
 * a stored value: the server-relative file route for a private reference
 * (session-checked there), the value itself otherwise. A malformed reference
 * becomes `null` rather than a link to nowhere.
 */
export function organizationFileBrowserPath(value: string | null): string | null {
  if (!isOrganizationFileReference(value)) return value;
  const parsed = parseOrganizationFileReference(value);
  if (parsed === null) return null;
  const query = new URLSearchParams({
    organizationId: parsed.organizationId,
    reference: formatOrganizationFileReference(parsed),
  });
  return `${ORGANIZATION_FILES_PATH}?${query.toString()}`;
}
